import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import Stack from "@mui/material/Stack";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Autocomplete, Grid, Button, TextField } from "@mui/material";
import Drawer from "../../Components/Common/DrawerLayout/Drawer";
import Form from "../../Components/Form/Form";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import {
  formValidator,
  isInvalidEmail,
  isInvalidPhoneNumber,
} from "../../Components/Form/validators";
import SelectRenderer from "../../Components/Form/SelectRenderer";
import { GET_CLUBS_SA, REGISTER_USER_SA } from "./SuperAdminService";
import { REGISTER_CLUB_USER } from "../../graphql/clubGraphql";
import { GET_CLUB_TEAMS } from "../../graphql/postGraphql";
import { AuthContext } from "../../context/AuthContext";
import { SnackBarContext } from "../../context/SnackBarContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { isSuperAdmin } from "../../utility/Utils";
import { getStorage } from "../../auth/utils";
import { USER_ROLES } from "../../utility/Enum";
import { getMissingStaffIdentityErrors } from "./staffRegistrationIdentity";
import { readStaffStorageScope } from "../Staff/staffUtils";
import {
  DrawerBody,
  DrawerFooter,
  DrawerLayout,
  DrawerSection,
  DrawerTitleHeader,
  getTransactionalDrawerPaperProps,
} from "../../Components/Common/DrawerLayout/DrawerLayout";

const staffRoles = [
  USER_ROLES.COACH,
  USER_ROLES.TEAM_ADMIN,
  USER_ROLES.CLUB_ADMIN,
];
const needsTeams = (role) =>
  role === USER_ROLES.COACH || role === USER_ROLES.TEAM_ADMIN;

const ClubSearchRenderer = ({
  label,
  name,
  onChange,
  disabled,
  error,
  storageScope,
  expectedFirebaseUid,
}) => {
  const [term, setTerm] = useState("");
  const [selectedClub, setSelectedClub] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { axiosGraphQL } = useGraphql();
  const snackBar = useContext(SnackBarContext);
  const services = useRef();
  services.current = { axiosGraphQL, snackBar };

  useEffect(() => {
    let active = true;
    const isCurrent = () =>
      active && readStaffStorageScope(getStorage()) === storageScope;
    setOptions([]);
    setLoading(false);
    if (term.trim().length < 3 || selectedClub) return;
    const timer = setTimeout(() => {
      if (!isCurrent() || !expectedFirebaseUid) return;
      setLoading(true);
      services.current
        .axiosGraphQL(
          { query: GET_CLUBS_SA, variables: { term: term.trim() } },
          { expectedFirebaseUid }
        )
        .then((response) => {
          if (isCurrent()) setOptions(response.data.data.getClubsSA || []);
        })
        .catch((searchError) => {
          if (!isCurrent()) return;
          services.current.snackBar.setOptions({
            alertMessage: searchError.message,
            alertSeverity: "error",
          });
          services.current.snackBar.setSnackbarOpen(true);
        })
        .finally(() => {
          if (isCurrent()) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [term, selectedClub, storageScope, expectedFirebaseUid]);

  return (
    <Autocomplete
      disabled={disabled}
      filterOptions={(clubs) => clubs}
      getOptionLabel={(club) => club.name || ""}
      inputValue={term}
      isOptionEqualToValue={(club, selected) => club._id === selected._id}
      loading={loading}
      onChange={(_event, club) => {
        setSelectedClub(club);
        setTerm(club?.name || "");
        onChange({ target: { name, value: club?._id || "" } });
      }}
      onInputChange={(_event, input, reason) => {
        if (reason !== "input") return;
        setTerm(input);
        setSelectedClub(null);
        onChange({ target: { name, value: "" } });
      }}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error}
          size="small"
        />
      )}
      value={selectedClub}
    />
  );
};

const schema = (
  selectedRole,
  t,
  globalSuperAdmin,
  teams,
  multipleTeams,
  clubId,
  storageScope,
  expectedFirebaseUid
) =>
  [
    {
      label: t("firstName"),
      name: "firstName",
      validators: [
        {
          validator: (value) => !value?.trim(),
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
        {
          validator: (value) => value?.trim().length > 100,
          text: () => t("registration_required_name"),
        },
      ],
      maxLength: 100,
      renderer: TextFieldRenderer,
      row: 1,
    },
    {
      label: t("lastName"),
      name: "lastName",
      maxLength: 100,
      validators: [
        {
          validator: (value) => value?.trim().length > 100,
          text: () => t("registration_required_name"),
        },
      ],
      renderer: TextFieldRenderer,
      row: 2,
    },
    {
      label: t("mobile"),
      name: "mobile",
      validators: [
        {
          validator: (value) =>
            Boolean(value) &&
            (!/^\+[1-9]\d{6,14}$/.test(value) || isInvalidPhoneNumber(value)),
          text: (_label) => `${t("error_invalid_phone_number")}`,
        },
      ],
      renderer: TextFieldRenderer,
      row: 3,
    },
    {
      label: t("email"),
      name: "email",
      validators: [
        {
          validator: isInvalidEmail,
          text: (_label) => `${t("error_invalid_email")}`,
        },
      ],
      renderer: TextFieldRenderer,
      row: 4,
    },
    {
      label: t("role"),
      name: "role",
      validators: [
        {
          validator: (value) => !staffRoles.includes(value),
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      options: [
        { text: t("coach"), value: USER_ROLES.COACH },
        { text: t("team_admin"), value: USER_ROLES.TEAM_ADMIN },
        { text: t("club_admin"), value: USER_ROLES.CLUB_ADMIN },
      ],
      renderer: SelectRenderer,
      row: 5,
    },
    globalSuperAdmin && {
      label: t("club_page_title"),
      name: "clubId",
      validators: [
        {
          validator: (value) => !value,
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      renderer: (props) => (
        <ClubSearchRenderer
          {...props}
          storageScope={storageScope}
          expectedFirebaseUid={expectedFirebaseUid}
        />
      ),
      row: 6,
    },
    needsTeams(selectedRole) && {
      label: t("selectTeam"),
      name: multipleTeams ? "teamIds" : "teamId",
      multiple: multipleTeams,
      disabled: !clubId,
      validators: [
        {
          validator: (value) => {
            const selected = multipleTeams ? value : [value];
            return (
              !selected?.length ||
              selected.some((id) => !teams.some((team) => team._id === id))
            );
          },
          text: (label) => `${label} ${t("cannot_be_empty")}`,
        },
      ],
      options: teams.map((team) => {
        return { text: team.name, value: team._id };
      }),
      renderer: SelectRenderer,
      row: 7,
    },
  ].filter(Boolean);

const initialState = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  role: "",
  clubId: "",
  teamId: "",
  teamIds: [],
};

const RegisterUserDrawer = ({
  openDrawer,
  onCloseDrawer,
  onExited,
  clubId,
  onRegistered,
  useStaffRegistration = false,
  teams: suppliedTeams,
}) => {
  const { authState, firebaseUser } = useContext(AuthContext);
  const [values, setValues] = useState(initialState);
  const snackBar = useContext(SnackBarContext);
  const [loadedTeams, setLoadedTeams] = useState({ clubId: null, teams: [] });
  const [pending, setPending] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const resetForm = useCallback(() => {
    setValues(initialState);
    setFormVersion((version) => version + 1);
    setRegistrationComplete(false);
  }, []);
  const requestVersion = useRef(0);
  const pendingRef = useRef(false);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const globalSuperAdmin =
    !useStaffRegistration && isSuperAdmin(authState.user);
  const activeClubId = clubId || authState.user?.club?._id || "";
  const selectedClubId = globalSuperAdmin ? values.clubId : activeClubId;
  const teams =
    useStaffRegistration && suppliedTeams !== undefined
      ? suppliedTeams
      : loadedTeams.clubId === selectedClubId
      ? loadedTeams.teams
      : [];
  const identityContextScope = JSON.stringify([
    authState.user?._id,
    authState.user?.roles,
    activeClubId,
    useStaffRegistration,
    firebaseUser?.uid,
  ]);
  const contextScope = JSON.stringify([identityContextScope, openDrawer]);
  // Editing the form must not adopt a new stored account or club before context updates.
  const storageSnapshot = useRef();
  if (storageSnapshot.current?.contextScope !== contextScope) {
    const storage = getStorage();
    storageSnapshot.current = {
      contextScope,
      storageScope: readStaffStorageScope(storage),
      expectedFirebaseUid: storage.getItem("firebaseUid") || "",
    };
  }
  const { storageScope, expectedFirebaseUid } = storageSnapshot.current;
  const scope = JSON.stringify([
    contextScope,
    storageScope,
    expectedFirebaseUid,
  ]);
  const formScope = JSON.stringify([
    identityContextScope,
    storageScope,
    expectedFirebaseUid,
  ]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const services = useRef();
  services.current = { axiosGraphQL, errorLog, snackBar };
  const isImpersonating = Boolean(getStorage().getItem("impersonate"));
  const _schema = schema(
    values.role,
    t,
    globalSuperAdmin,
    teams,
    useStaffRegistration,
    selectedClubId,
    storageScope,
    expectedFirebaseUid
  ).map((field) => ({ ...field, disabled: pending || field.disabled }));
  const identitySchema = _schema.filter((field) =>
    ["firstName", "lastName", "mobile", "email"].includes(field?.name)
  );
  const accessSchema = _schema.filter(
    (field) =>
      field?.name &&
      !["firstName", "lastName", "mobile", "email"].includes(field.name)
  );

  useEffect(() => {
    requestVersion.current += 1;
    pendingRef.current = false;
    setPending(false);
    return () => {
      requestVersion.current += 1;
    };
  }, [scope]);

  // Account/club changes clear immediately; ordinary closing retains the form.
  useEffect(() => {
    resetForm();
    setLoadedTeams({ clubId: null, teams: [] });
  }, [formScope, resetForm]);

  useEffect(() => {
    // Super Admin can keep registering; Staff closes after its success callback.
    if (registrationComplete && openDrawer) resetForm();
  }, [registrationComplete, openDrawer, resetForm]);

  useEffect(() => {
    if (!openDrawer) return;
    let active = true;
    const isCurrent = () =>
      active && readStaffStorageScope(getStorage()) === storageScope;
    setLoadedTeams({ clubId: null, teams: [] });
    if (
      isCurrent() &&
      expectedFirebaseUid &&
      openDrawer &&
      selectedClubId &&
      !(useStaffRegistration && suppliedTeams !== undefined)
    ) {
      services.current
        .axiosGraphQL(
          {
            query: GET_CLUB_TEAMS,
            variables: {
              clubId: selectedClubId,
              skip: 0,
              limit: 500,
            },
          },
          { expectedFirebaseUid }
        )
        .then((response) => {
          const { ClubTeams } = response.data.data;
          if (isCurrent())
            setLoadedTeams({ clubId: selectedClubId, teams: ClubTeams || [] });
        })
        .catch((error) => {
          if (!isCurrent()) return;
          services.current.errorLog(
            error,
            "RegisterUserDrawer",
            "useEffect",
            "GET_CLUB_TEAMS"
          );
          services.current.snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          services.current.snackBar.setSnackbarOpen(true);
        });
    }
    return () => {
      active = false;
    };
  }, [
    openDrawer,
    selectedClubId,
    useStaffRegistration,
    suppliedTeams,
    scope,
    storageScope,
    expectedFirebaseUid,
  ]);

  const getRolesTranslated = (role, isError) => {
    switch (role) {
      case USER_ROLES.CLUB_ADMIN:
        return isError ? t("error_creating_club_admin") : t("the_club_admin");
      case USER_ROLES.TEAM_ADMIN:
        return isError ? t("error_creating_team_admin") : t("the_team_admin");
      case USER_ROLES.COACH:
        return isError ? t("error_creating_coach") : t("the_coach");
    }
  };

  const handleSuccess = (message) => {
    snackBar.setOptions({
      alertMessage: message,
      alertSeverity: "success",
    });
    snackBar.setSnackbarOpen(true);
    setRegistrationComplete(true);
  };

  const handleError = (error, queryName) => {
    errorLog(error, "RegisterUserDrawer", "handleSubmit", queryName);
    snackBar.setOptions({
      alertMessage: error.message,
      alertSeverity: "error",
    });
    snackBar.setSnackbarOpen(true);
  };

  const handleSubmit = async () => {
    if (
      pendingRef.current ||
      !openDrawer ||
      readStaffStorageScope(getStorage()) !== storageScope
    )
      return;
    if (
      !expectedFirebaseUid ||
      (firebaseUser?.uid && firebaseUser.uid !== expectedFirebaseUid)
    ) {
      handleError(new Error(t("session_identity_changed")), "REGISTER_USER");
      return;
    }
    const errors = {
      ...formValidator({ schema: _schema, values }),
      ...getMissingStaffIdentityErrors(values, t),
    };
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
      return;
    }
    if (!selectedClubId) return;
    const query = globalSuperAdmin ? REGISTER_USER_SA : REGISTER_CLUB_USER;
    const queryName = globalSuperAdmin
      ? "REGISTER_USER_SA"
      : "REGISTER_CLUB_USER";
    const variables = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      mobile: values.mobile.trim(),
      role: values.role,
      clubId: selectedClubId,
      ...(useStaffRegistration
        ? { teamIds: needsTeams(values.role) ? values.teamIds : [] }
        : { teamId: needsTeams(values.role) ? values.teamId : "" }),
    };
    const version = ++requestVersion.current;
    const isCurrent = () =>
      requestVersion.current === version &&
      currentScope.current === scope &&
      readStaffStorageScope(getStorage()) === storageScope;
    pendingRef.current = true;
    setPending(true);
    try {
      const response = await axiosGraphQL(
        { query, variables },
        { expectedFirebaseUid }
      );
      if (!isCurrent()) return;
      const result = response?.data?.data;
      const registeredUser = globalSuperAdmin
        ? result?.RegisterUserSa
        : result?.registerClubUser;
      if (!registeredUser?._id)
        throw new Error(getRolesTranslated(values.role, true));
      handleSuccess(
        `${getRolesTranslated(values.role)} ${t("created_successfully")}`
      );
      onRegistered?.(registeredUser);
    } catch (error) {
      if (isCurrent()) handleError(error, queryName);
    } finally {
      if (isCurrent()) {
        pendingRef.current = false;
        setPending(false);
      }
    }
  };

  const onChange = ({ target: { name, value } }) => {
    if (pendingRef.current) return;
    setValues((prevState) => ({
      ...prevState,
      [name]: name === "mobile" ? value?.replace(/\s/g, "") : value,
      ...(name === "clubId" || name === "role"
        ? { teamId: "", teamIds: [] }
        : {}),
      errors: {},
    }));
  };

  const handleCloseDrawer = () => {
    requestVersion.current += 1;
    pendingRef.current = false;
    setPending(false);
    onCloseDrawer();
  };

  const handleExited = () => {
    if (openDrawer) return;
    resetForm();
    setLoadedTeams({ clubId: null, teams: [] });
    onExited?.();
  };

  return (
    <Drawer
      anchor="right"
      open={openDrawer}
      onClose={handleCloseDrawer}
      SlideProps={{ onExited: handleExited }}
      PaperProps={getTransactionalDrawerPaperProps(
        "register-user-drawer-title",
        "wide"
      )}
    >
      <DrawerLayout sx={{ pt: isImpersonating ? 3 : 0 }}>
        <DrawerTitleHeader
          closeLabel={t("close")}
          onClose={handleCloseDrawer}
          subtitle={t("register_user_description")}
          title={t(useStaffRegistration ? "add_staff" : "register_user")}
          titleId="register-user-drawer-title"
        />
        <DrawerBody key={formVersion} data-testid="register-user-drawer-body">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DrawerSection
                description={t("user_identity_description")}
                title={t("user_identity_title")}
                titleId="user-identity-section-title"
              >
                <Form
                  schema={identitySchema}
                  values={values}
                  onChange={onChange}
                  errors={values.errors}
                />
              </DrawerSection>
            </Grid>
            <Grid item xs={12}>
              <DrawerSection
                description={t(
                  useStaffRegistration
                    ? "staff_join_instructions"
                    : "user_access_description"
                )}
                title={t("user_access_title")}
                titleId="user-access-section-title"
              >
                <Form
                  schema={accessSchema}
                  values={values}
                  onChange={onChange}
                  errors={values.errors}
                />
              </DrawerSection>
            </Grid>
          </Grid>
        </DrawerBody>
        <DrawerFooter data-testid="register-user-drawer-footer">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" fullWidth onClick={handleCloseDrawer} disabled={pending}>
              {t("cancel")}
            </Button>
            <Button
              startIcon={<AddOutlinedIcon />}
              onClick={handleSubmit}
              disabled={pending || !openDrawer || (!globalSuperAdmin && !selectedClubId)}
              fullWidth
            >
              {t("create")}
            </Button>
          </Stack>
        </DrawerFooter>
      </DrawerLayout>
    </Drawer>
  );
};

export default RegisterUserDrawer;
